import { useContext, useEffect } from 'react';
import { articlesOptions, issuesOptions } from '@shared/api';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';

import config from '@config';
import { AppContext } from '@/App.context';
import {
	Issue,
	getImagesFromIssue,
	Article,
	USER_ROLE,
} from '@/constants';

import { useServiceWorker } from './use-service-worker';

/*
	Redirect to base url if the issue is not found
*/

export function useIssueNotFound(issue?: Issue | null) {
	const navigate = useNavigate();
	const { baseUrl, onlineStatus: isOnline } = useContext(AppContext);
	useEffect(() => {
		// A null issue while offline just means "not cached", not "doesn't
		// exist" -- getIssue()/useGetIssue() resolve with null in that case
		// specifically so the view can show an offline message on the
		// current route (OfflineContent), not redirect away from it. Only
		// redirect once that's a confirmed not-found: online, and the
		// server still says there's nothing there.
		if (issue === null && isOnline) {
			navigate({
				to: baseUrl,
				replace: true,
			});
		}
	}, [issue, navigate, baseUrl, isOnline]);
}

export function useIssues(args?: GetIssuesArgs): GetIssuesResponse {
	const isAdmin = localStorage.getItem(USER_ROLE) === 'admin';
	const { parseGraphQLClientError, onlineStatus: isOnline } =
		useContext(AppContext);

	const { data, isPending, error } = useQuery(
		issuesOptions(
			{
				sortKey: args?.sortKey,
				limit: args?.limit,
				reverse: args?.reverse,
				offset: args?.offset,
			},
			{
				isOnline,
				isAdmin,
				ttlMinutes: config.app?.cache?.ttl?.getAllIssues,
			},
		),
	);

	// The schema types `issues` as a list of *nullable* items ([Issue]!);
	// drop any null entries rather than let them flow downstream as if
	// they were valid issues.
	//
	// The `as unknown as Array<Issue>` below is a known, documented gap, not
	// a silent one: the generated type is honest that description/cover/
	// index/slug/externalUrl/isFree can be null (matching the real schema),
	// while the hand-rolled `Issue` interface in constants.ts claims they're
	// required. Reconciling that is topic 5's "derive Issue/Article from
	// generated types" step (improvements/05-typescript-graphql-type-safety.md) —
	// a deliberate, separate migration, not something to patch field-by-field
	// here. This cast keeps runtime behaviour exactly as it was before this
	// query was typed at all (the old `graphqlFetch<{ issues: Array<Issue> }>`
	// was itself an unchecked assertion of the same claim); it does not make
	// the gap worse, just visible.
	const issues = data?.issues?.filter(
		(issue): issue is NonNullable<typeof issue> => issue != null,
	) as unknown as Array<Issue> | undefined;

	useDownloadInBackground(issues);

	return {
		issues: issues ? issues.sort(byReleaseDate) : [],
		isLoading: isPending,
		error: error ? parseGraphQLClientError(error) : null,
	};
}

interface GetIssuesArgs {
	sortKey?: IssueSortKeys;
	limit?: number;
	reverse?: boolean;
	offset?: number;
}

type IssueSortKeys = 'RELEASE_DATE' | 'CREATED' | 'UPDATED' | 'POSITION';

export interface GetIssuesResponse {
	issues: Array<Issue>;
	isLoading: boolean;
	error?: Error | null;
}

export function useDownloadInBackground(issues?: Array<Issue>) {
	const { isReady, sendMessage } = useServiceWorker();
	useEffect(() => {
		if (!isReady) return;
		if (!issues) return;
		if (!issues.length) return;
		const images: Set<string> = new Set();
		getImagesFromIssue(images, issues);
		if (images.size) {
			sendMessage({
				type: 'DOWNLOAD_BACKGROUND_IMAGES',
				payload: {
					images: [...images],
				},
			});
		}
	}, [isReady, issues, sendMessage]);
}

export function useArticles(args?: ArticleArgs): GetArticlesResponse {
	const { parseGraphQLClientError, onlineStatus: isOnline } =
		useContext(AppContext);
	const isFeatured = !!args?.isFeatured;
	const isAdmin = localStorage.getItem(USER_ROLE) === 'admin';

	const { isPending, error, data } = useQuery(
		articlesOptions(
			{ isFeatured },
			{
				isOnline,
				isAdmin,
				ttlMinutes: config.app?.cache?.ttl?.getFeatureArticles,
			},
		),
	);

	// Same as issues above: [Article]! allows null items in the list, and
	// the same documented `as unknown as` gap applies (see useIssues) —
	// generated nullability is honest, the hand-rolled Article type isn't
	// yet; topic 5 owns reconciling that.
	const articles = data?.articles?.filter(
		(article): article is NonNullable<typeof article> => article != null,
	) as unknown as Array<Article> | undefined;

	return {
		articles: articles ?? [],
		isLoading: isPending,
		error: error ? parseGraphQLClientError(error) : null,
	};
}

export interface GetArticlesResponse {
	articles: Article[];
	isLoading: boolean;
	error?: Error | null;
}

export function byReleaseDate(a: Issue, b: Issue) {
	if (a.releaseDate && b.releaseDate) {
		return (
			new Date(`${b.releaseDate}`).getTime() -
			new Date(`${a.releaseDate}`).getTime()
		);
	}
	return (
		new Date(`${b.updated}`).getTime() - new Date(`${a.updated}`).getTime()
	);
}

interface ArticleArgs {
	isFeatured?: boolean;
}
